import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Loader from '../../../general/Loader';
import { className } from '../../../../lib/className';
import { fc, useI18n } from '../../../../../i18n';
import Address from '../../../general/location/Address';
import DatetimeField from '../../../general/datetime/DatetimeField';
import ScrollList from '../../../general/list/ScrollList';
import ReportSummary from './ReportSummary';
import ReportHeader from './ReportHeader';
import { formatter } from '../../../../misc/datetime';
import { actions } from '../../../../redux/api/reports';
import CheckIntervalMap from './CheckIntervalMap';
import SwitchMapMenuItem from './SwitchMapMenuItem';
import NoDataView from '../../../general/view/NoDataView';
import { scrollReportItemIntoView } from '../../../../misc/misc';
import { getAssetType } from 'core/misc/assetTypes';

import './idling.scss';

function HeaderListItem(props) {
	const { f } = useI18n();
	return (
		<>
			<div className="header-item">
				<span className="capitalize">{f('item No.')}</span>
			</div>
			{props.withGroup && (
				<div className="header-item">
					<span className="capitalize">{f('group')}</span>
				</div>
			)}
			{props.isAssetTypes && (
				<div className="header-item">
					<span className="capitalize">{fc('asset type')}</span>
				</div>
			)}
			<div className="header-item">
				<span className="capitalize">{f('start time')}</span>
			</div>
			<div className="header-item">
				<span className="capitalize">{f('start location')}</span>
			</div>
			<div className="header-item">
				<span className="capitalize">{f('end time')}</span>
			</div>
			<div className="header-item">
				<span className="capitalize">{f('end location')}</span>
			</div>
			<div className="header-item">
				<span className="capitalize">{f('duration')}</span>
			</div>
		</>
	);
}

function ReportListItem(props) {
	const onClick = () => {
		props.onClick(props.interval, props.index);
	};

	const _className = className('clickable', { selected: props.selected });
	return (
		<>
			<div ref={props.customRef} className={_className} onClick={onClick}>
				{props.index}
			</div>
			{!!props.group && <div className={_className}>{props.group}</div>}
			{!!props.assetType && <div className={_className}>{props.assetType}</div>}
			<div className={_className} onClick={onClick}>
				<DatetimeField datetime={props.interval.since.generatedAt} />
			</div>
			<div className={_className} onClick={onClick}>
				<Address message={props.interval.since} full />
			</div>
			<div className={_className} onClick={onClick}>
				{props.interval.until && (
					<DatetimeField datetime={props.interval.until.generatedAt} now={props.interval.since.generatedAt} />
				)}
			</div>
			<div className={_className} onClick={onClick}>
				<Address message={props.interval.until} full />
			</div>
			<div className={_className} onClick={onClick}>
				{props.interval.until && formatter.duration(props.interval.since.generatedAt, props.interval.until.generatedAt)}
			</div>
		</>
	);
}

function EodListItem() {
	const { f } = useI18n();
	return (
		<div className="indicator">
			<span className="capitalize">{f('you have reached the end')}</span>
		</div>
	);
}

// -----------------------------------------------------------------

function Idling(props) {
	const { devices, state, categoryMap } = props;
	const { uri, parameters, list, hasMore, pending, error } = state;

	const { f } = useI18n();
	const dispatch = useDispatch();

	const selectedItemBox = useRef();
	const [displayMap, setDisplayMap] = useState(false);
	const [selectedInterval, setSelectedinterval] = useState({}); // { [index]: interval }

	const assetType = parameters.isAssetTypes ? getAssetType(devices?.[uri]?.categoryIds)?.getName() || '-' : null;

	const group = useMemo(() => {
		const device = devices?.[uri];
		let deviceGroup = '-';

		if (!device?.categoryIds) {
			return deviceGroup;
		}

		device.categoryIds.forEach((categoryId) => {
			const category = categoryMap[categoryId];
			const comprisingIds = category?.comprisingIds;

			if (comprisingIds) {
				if (parameters.groupingId && comprisingIds.includes(parameters.groupingId)) deviceGroup = category.name;
			}
		});

		return deviceGroup;
	}, [devices, state, categoryMap]);

	useLayoutEffect(() => {
		if (selectedItemBox.current) {
			scrollReportItemIntoView(selectedItemBox.current);
		}
	}, [selectedInterval, displayMap]);

	const loadNext = () => {
		dispatch(actions.idling.request({ uri, parameters }));
	};

	const onItemClick = (interval, index) => {
		setSelectedinterval({ [index]: interval });
		if (!displayMap) setDisplayMap(true);
	};

	const switchMapHandler = () => {
		setDisplayMap(!displayMap);
	};

	let content = null;
	let mapBox = null;
	let canExport = false;
	if (list != null) {
		if (!list || list.length == 0) {
			content = <NoDataView message={f('no data for such criteria')} />;
		} else {
			canExport = true;
			let items = list.map((interval, at) => {
				const index = at + 1;
				const isSelected = Object.keys(selectedInterval)[0] == index;
				return (
					<ReportListItem
						key={at}
						onClick={onItemClick}
						selected={isSelected}
						customRef={isSelected ? selectedItemBox : undefined}
						interval={interval}
						index={index}
						group={parameters.groupingId ? group : null}
						assetType={assetType}
					/>
				);
			});
			items.unshift(<HeaderListItem key={1000000} withGroup={!!parameters.groupingId} isAssetTypes={!!parameters.isAssetTypes} />);
			content = (
				<ScrollList pending={pending} next={loadNext} isEod={!hasMore} eodItem={EodListItem}>
					{items}
				</ScrollList>
			);
			if (displayMap) {
				mapBox = <CheckIntervalMap interval={Object.values(selectedInterval)[0]} />;
			}
		}
	} else {
		content = (
			<div className="center">{error ? <span className="error">{error}</span> : <Loader size={Loader.Size.MD} />}</div>
		);
	}

	const exportReport = () => {
		props.dispatch(actions.idling.export({ uri: props.state.uri }));
		props.history.goBack();
	};

	const timeRange = props.state.parameters.timeRange;
	return (
		<div className={className("report idling", {
			"with-group": !!parameters.groupingId,
			"with-asset-type": !!parameters.isAssetTypes
		})}>
			<ReportHeader
				title={f('idling report')}
				canExport={canExport}
				onExport={exportReport}
				items={<SwitchMapMenuItem disabled={!canExport} active={displayMap} onClick={switchMapHandler} />}
			/>
			<ReportSummary
				since={timeRange.since}
				until={timeRange.until}
				devices={[props.devices[props.state.uri]]}
				generatedAt={props.state.at}
			/>
			<div className={className('content', { 'with-map': displayMap })}>
				{content}
				{mapBox}
			</div>
		</div>
	);
}

export default withRouter(
	connect((state) => {
		return {
			state: state.reports.idling,
			devices: state.devices.map,
			categoryMap: state.categories.general.map,
		};
	})(Idling),
);
